import React, {useEffect, useState, useCallback} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import PageBase from "../../../components/PageBase/index";
import Datatable from "../../../components/Datatable/index";
import LoadingPanel from "../../../components/LoadingPanel/index";
import Pagination from "../../../components/Pagination/index";
import Button from "../../../components/Button/index";
import CreateLicenseModal from "../../../components/CreateLicenseModal/index";
import ConfirmDeleteModal from "../../../components/ConfirmDeleteModal/index";
import columns from './column.js';
import useColumnFilters from '../../../hooks/useColumnFilters.js';
import * as licenseActions from "../../../redux/License/actions";
import './License.scss';

const License = props => {
	const [loading, setLoading] = useState(true);
	const [currentPage, setCurrentPage] = useState(1);
	const [pageSize, setPageSize] = useState(10);
	const [showCreateModal, setShowCreateModal] = useState(false);
	const [showDeleteModal, setShowDeleteModal] = useState(false);
	const [licenseToDelete, setLicenseToDelete] = useState(null);
	const [deleteLoading, setDeleteLoading] = useState(false);
	const dispatch = useDispatch();
	const licenses = useSelector(state => state.license).licenses;
	const metadata = useSelector(state => state.license).metadata;

	const {
		filters,
		sortBy,
		sortOrder,
		filteredData,
		handleFilterChange,
		updateData,
		uniqueValuesByColumn,
		filterOrder
	} = useColumnFilters({
		data: licenses,
		columns,
		storageKey: `licenses`,
		initialState: {}
	});

	useEffect(() => {
		const load = async () => {
			await dispatch(licenseActions.loadLicenses(currentPage, pageSize));
			setLoading(false);
		};
		load();
	}, [dispatch, currentPage, pageSize]);

	useEffect(() => {
		updateData(licenses);
	}, [licenses, updateData]);

	// Ensure delete modal state is properly initialized
	useEffect(() => {
		if (!showDeleteModal) {
			setLicenseToDelete(null);
			setDeleteLoading(false);
		}
	}, [showDeleteModal]);

	// Ensure delete modal is properly initialized on mount
	useEffect(() => {
		setShowDeleteModal(false);
		setLicenseToDelete(null);
		setDeleteLoading(false);
	}, []);

	const handlePageChange = (newPage) => {
		setCurrentPage(newPage);
		setLoading(true);
	};

	const handlePageSizeChange = (newPageSize) => {
		setPageSize(newPageSize);
		setCurrentPage(1); // Reset to first page when changing page size
		setLoading(true);
	};

	const handleCreateSuccess = () => {
		// Reload licenses after creating a new one
		dispatch(licenseActions.loadLicenses(currentPage, pageSize));
	};

	const handleDeleteClick = useCallback((license) => {
		setLicenseToDelete(license);
		setShowDeleteModal(true);
	}, []);

	// Add event listener for delete buttons
	useEffect(() => {
		const handleDeleteButtonClick = (event) => {
			if (event.target.closest('.delete-license-btn')) {
				event.preventDefault();
				event.stopPropagation();
				
				const button = event.target.closest('.delete-license-btn');
				const licenseId = button.getAttribute('data-license-id');
				
				// Find the license data from the current licenses
				const license = licenses.find(l => l.id.toString() === licenseId);
				if (license) {
					handleDeleteClick(license);
				}
			}
		};

		document.addEventListener('click', handleDeleteButtonClick);
		
		return () => {
			document.removeEventListener('click', handleDeleteButtonClick);
		};
	}, [licenses, handleDeleteClick]);

	const handleDeleteConfirm = async () => {
		if (!licenseToDelete) {
			return;
		}
		
		setDeleteLoading(true);
		try {
			const result = await dispatch(licenseActions.deleteLicense(licenseToDelete.id));
			if (result && result.status === 'ok') {
				// Reload licenses to update the list
				await dispatch(licenseActions.loadLicenses(currentPage, pageSize));
				setShowDeleteModal(false);
				setLicenseToDelete(null);
			}
		} catch (error) {
			console.error('Error deleting license:', error);
		} finally {
			setDeleteLoading(false);
		}
	};

	const handleDeleteCancel = () => {
		setShowDeleteModal(false);
		setLicenseToDelete(null);
	};

	// Use original columns without modification
	const columnsWithActions = columns;

	if(loading) {
		return (
			<PageBase title="License Management" removeMargins>
				<LoadingPanel />
			</PageBase>
		)
	}

	const totalPages = metadata?.totalPages || 1;
	const totalItems = metadata?.total || 0;

	return (
		<PageBase title="License Management" removeMargins>
			<div style={{margin: '0 30px'}}>
				{/* Header with Create New License Button */}
				<div style={{
					display: 'flex',
					justifyContent: 'flex-end',
					alignItems: 'center',
					marginBottom: '20px',
					padding: '16px 0',
					borderBottom: '1px solid #e0e0e0'
				}}>
					<Button
						type="blue"
						center
						style={{ minWidth: 70, height: 35, borderRadius: 45, fontWeight: 700, fontSize: 14 }}
						onClick={() => setShowCreateModal(true)}
					>
						Create New License
					</Button>
				</div>

				<Datatable 
					gridHeight="calc(100vh - 280px)"
					columns={columnsWithActions}
					data={filteredData}
					sortBy={sortBy}
					sortOrder={sortOrder}
					filters={filters}
					onFilterChange={handleFilterChange}
					filterOrder={filterOrder}
					uniqueValuesByColumn={uniqueValuesByColumn}
					allData={licenses}
				/>
				
				{/* Pagination Controls */}
				<div>
					<Pagination
						showPageSizeSelector={false}
						currentPage={currentPage}
						totalPages={totalPages}
						totalItems={totalItems}
						pageSize={pageSize}
						onPageChange={handlePageChange}
						onPageSizeChange={handlePageSizeChange}
					/>
				</div>
			</div>

			{/* Create License Modal */}
			<CreateLicenseModal
				visible={showCreateModal}
				onClose={() => setShowCreateModal(false)}
				onSuccess={handleCreateSuccess}
			/>

			{/* Delete Confirmation Modal */}
			{showDeleteModal && licenseToDelete && (
				<ConfirmDeleteModal
					visible={showDeleteModal}
					onClose={handleDeleteCancel}
					onConfirm={handleDeleteConfirm}
					title="Delete License"
					message="Are you sure you want to delete the license?"
					loading={deleteLoading}
				/>
			)}
		</PageBase>
	)
};

export default License; 